import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquareText, Sparkles } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import * as asuntosApi from '@/api/asuntos.api';
import { HojaDelAsunto } from '@/components/athena/HojaDelAsunto';
import { TarjetaDeAsunto } from '@/components/athena/TarjetaDeAsunto';
import { plataPorMoneda, enlaceWhatsApp } from '@/lib/athena';
import { useUiStore } from '@/store/ui.store';

/**
 * LA FICHA DE UNA PERSONA — todo lo de alguien, en un solo lugar.
 *
 * ═══ "RESPONDER EN WHATSAPP" ABRE, NO ENVÍA ═══
 *
 * Regla dura del mandato: la app nunca manda un mensaje. Este botón arma el
 * enlace `wa.me` y abre el chat; escribir y enviar lo hace el dueño, en
 * WhatsApp. No hay caja de escribir en ningún lado de este producto.
 */

export default function FichaDePersona() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const abrirAthena = useUiStore((s) => s.abrirAthena);
  const [abierto, setAbierto] = useState<string | null>(null);

  const asuntos = useQuery({
    queryKey: ['asuntos', 'persona', id],
    queryFn: () => asuntosApi.getAsuntos({ estado: 'todos', limite: 200 }),
    enabled: Boolean(id),
  });

  const suyos = (asuntos.data?.asuntos ?? []).filter((a) => a.contactId === id);
  const abiertos = suyos.filter((a) => !['CUMPLIDO', 'DESCARTADO'].includes(a.estado));
  const persona = suyos[0];
  const debe = plataPorMoneda(
    suyos.filter((a) => a.cajon === 'DINERO' && a.montoCentavos && a.estado !== 'CUMPLIDO'),
  );

  const wa = enlaceWhatsApp(persona?.contact?.phone ?? null);

  return (
    <div className="space-y-3">
      {/* Flecha atrás arriba a la izquierda, como pide el mandato para toda
          pantalla secundaria. */}
      <button
        onClick={() => navegar(-1)}
        className="flex min-h-[44px] items-center gap-1 text-sm text-slate-600 dark:text-slate-300"
      >
        <ArrowLeft className="h-4 w-4" /> Atrás
      </button>

      {asuntos.isPending ? (
        <Spinner />
      ) : !persona ? (
        <Card>
          <CardBody className="py-10 text-center text-sm text-slate-400">
            No hay nada registrado de esta persona todavía.
          </CardBody>
        </Card>
      ) : (
        <>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
              {persona.conQuien}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {abiertos.length} {abiertos.length === 1 ? 'cosa abierta' : 'cosas abiertas'}
              </Badge>
              {debe.map((t) => (
                <Badge
                  key={t}
                  className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
                >
                  Te debe {t}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer">
                <Button>
                  <MessageSquareText className="h-4 w-4" /> Responder en WhatsApp
                </Button>
              </a>
            )}
            <Button variant="secondary" onClick={abrirAthena}>
              <Sparkles className="h-4 w-4" /> Preguntarle a Athena por {persona.conQuien}
            </Button>
          </div>

          <Card>
            <CardBody className="space-y-0">
              <p className="mb-1 text-sm font-semibold text-slate-700 dark:text-slate-200">
                Sus asuntos
              </p>
              {suyos.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">Nada registrado.</p>
              ) : (
                suyos.map((a) => (
                  <TarjetaDeAsunto key={a.id} asunto={a} conFecha conGestos={false} onAbrir={setAbierto} />
                ))
              )}
            </CardBody>
          </Card>
        </>
      )}

      <HojaDelAsunto asuntoId={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}
